/**
 * Base HTTP client: adds Authorization, on 401 → redirect to login.
 * On any API error — shows toast in top right (via toastStore).
 * Supports realms (admin, user, tester) — separate tokens and login pages.
 */
import {
  canonicalRealmForRole,
  getRealm,
  getRealmUrl,
  REALM_LOGIN_PATHS,
  REALM_TOKEN_KEYS,
} from '../lib/realm.js';
import { showToast } from '../lib/toastStore.js';
import { getT } from '../i18n.jsx';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

/** API path prefix. Models, media, chats — under /admin; login — /admin, /user, /test. */
export const API_PATH_ADMIN = '/admin';

export function getToken(realm) {
  const storedRole = localStorage.getItem('auth_role') || sessionStorage.getItem('auth_role');
  const r = canonicalRealmForRole(storedRole || realm || getRealm());
  const key = REALM_TOKEN_KEYS[r];
  const scoped = localStorage.getItem(key) || sessionStorage.getItem(key);
  if (scoped) return scoped;

  // One-time migration for sessions created before role-scoped storage.
  if (storedRole) {
    const localLegacy = localStorage.getItem('access_token');
    const sessionLegacy = sessionStorage.getItem('access_token');
    const legacy = localLegacy || sessionLegacy;
    if (legacy) {
      const store = localLegacy ? localStorage : sessionStorage;
      store.setItem(key, legacy);
      localStorage.removeItem('access_token');
      sessionStorage.removeItem('access_token');
      return legacy;
    }
  }
  return null;
}

export function setToken(token, realm, { remember = true } = {}) {
  const r = canonicalRealmForRole(realm || getRealm());
  const key = REALM_TOKEN_KEYS[r];
  if (token) {
    const store = remember ? localStorage : sessionStorage;
    store.setItem(key, token);
  } else {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  }
}

export function isAuthenticated(realm) {
  return !!getToken(realm);
}

export function clearAuthSession() {
  Object.values(REALM_TOKEN_KEYS).forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
  localStorage.removeItem('access_token');
  localStorage.removeItem('auth_role');
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('auth_role');
}

export function storeAuthSession(payload = {}, { remember = true, realm } = {}) {
  const token = payload.access_token || payload.token || '';
  const role = payload.role || realm || getRealm();
  if (!token) return;
  clearAuthSession();
  const store = remember ? localStorage : sessionStorage;
  store.setItem('auth_role', role);
  store.setItem(REALM_TOKEN_KEYS[canonicalRealmForRole(role)], token);
}

export function hasValidStoredAuthSession() {
  return isAuthenticated(getRealm());
}

export function redirectToLogin(realm) {
  const r = realm ?? getRealm();
  clearAuthSession();
  const path = REALM_LOGIN_PATHS[r] || '/login';
  window.location.href = getRealmUrl(r, path);
}

/**
 * Unified request entry point: adds Authorization, on 401 — redirect.
 * Token is selected by current pathname (realm).
 * @returns {Promise<Response>}
 */
export async function api(url, options = {}) {
  const token = getToken();
  const skipContentType = options.skipContentType;
  const suppressErrorToast = Boolean(options.suppressErrorToast);
  const headers = {
    ...options.headers,
  };
  if (!skipContentType) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const {
    skipContentType: _,
    suppressErrorToast: _suppressErrorToast,
    ...restOptions
  } = options;
  const res = await fetch(`${API_BASE}${url}`, { ...restOptions, headers, cache: 'no-store' });
  if (res.status === 401) {
    redirectToLogin();
    throw new Error(getT()('errors.sessionExpired'));
  }
  if (!res.ok) {
    const payload = res.clone ? await res.clone().json().catch(() => ({})) : {}
    const msg = await parseErrorBody(res).catch(() => getT()('errors.request'));
    if (!suppressErrorToast) showToast(msg, 'error');
    const error = new Error(msg);
    error.status = res.status;
    error.detail = payload.detail;
    throw error;
  }
  return res;
}

export async function parseErrorBody(res) {
  if (res?.status === 413) {
    return 'Файл слишком большой. Текущий лимит загрузки — 250 MB. Сожмите файл, разделите видео или загрузите через серверный импорт.';
  }
  const d = await res.json().catch(() => ({}));
  if (d.detail) {
    const detail = d.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail[0]?.msg ?? String(detail);
    if (detail.message && Array.isArray(detail.errors))
      return detail.message + '\n' + detail.errors.join('\n');
    return detail.message ?? String(detail);
  }
  if (d.message) return d.message;
  return getT()('errors.request');
}

export async function ensureOk(res, fallbackMessage) {
  if (!res.ok) {
    const msg = await parseErrorBody(res).catch(() => fallbackMessage);
    showToast(msg, 'error');
    throw new Error(msg);
  }
}

/**
 * Request with res.ok check + automatic JSON parsing.
 * On error — toast + throw.
 */
export async function apiJson(url, options = {}, errorMessage) {
  const res = await api(url, options);
  await ensureOk(res, errorMessage || getT()('errors.request'));
  return res.json();
}

/**
 * DELETE request. 204 or empty response — success.
 * On error — toast + throw.
 */
export async function apiDelete(url, errorMessage) {
  const res = await api(url, { method: 'DELETE' });
  await ensureOk(res, errorMessage || getT()('errors.request'));
}

/**
 * Extract error message from API response.
 * @param {Error} err — error thrown by api/ensureOk (message = detail from response)
 * @param {string} [fallback] — fallback text if no message
 * @returns {string}
 */
export function getApiErrorMessage(err, fallback) {
  if (!err) return fallback || getT()('errors.request')
  const msg = err?.message
  return typeof msg === 'string' && msg.trim() ? msg.trim() : (fallback || getT()('errors.request'))
}

export { API_BASE };
